import type { ReactNode } from 'react'
import { CompanyIcon, type Verdict, VerdictPill } from '../../shared/pills'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type {
  CompanyPacingRow,
  PacingAvgTile as PacingAvgTileData,
  PacingData,
  ReviewComment,
} from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Pacing sections (spec §8.4). Two components, one per scope:
 *
 * `PacingSection` — the base benchmark layout used by system-design (and any
 * future non-coding type; behavioral's pacing-equivalent, "Delivery", is a
 * distinct component per spec §8.5/§16 — out of scope here). 2-col grid: big
 * monospace TOTAL tile beside company target ranges (1–3 rows — must render
 * correctly with a single row, e.g. the SD/behavioral seeds), then
 * topic/subtopic avg tiles below. Every tile/row is a comment target (spec
 * §8.4). LG-632 leaves this base section untouched — same composer targets,
 * same typography — because the ticket strips coding surfaces only.
 *
 * `CodingPacingSection` — the coding-only card (LG-632, spec D17). Coding
 * pacing benchmarks and the total's note are cut because no stored field
 * backs them, so the component takes the stored duration alone and renders
 * TOTAL plus that duration and nothing else — it structurally cannot show a
 * note, a band, or an average.
 *
 * `PacingVerdict` fixture strings ('Faster'/'In range'/'Over range'/'Far
 * over') map 1:1 onto F1's shared `Verdict` union — `VerdictPill` renders
 * the exact same label text via its own `VERDICT_META`, so no `label` prop
 * override is needed here.
 */
const VERDICT_KEY: Record<CompanyPacingRow['verdict'], Verdict> = {
  Faster: 'faster',
  'In range': 'in-range',
  'Over range': 'over-range',
  'Far over': 'far-over',
}

export function PacingSection(props: {
  pacing: PacingData
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { pacing, comments = [], anchorRef } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'pacing')

  return (
    <SectionCard
      title="Pacing"
      commentCount={sectionComments.length}
      onAddComment={() => openComposer('pacing', 'Pacing · Overall', 'praise')}
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="mb-2.5 grid grid-cols-[1fr_1.6fr] gap-2.5">
        <button
          type="button"
          onClick={() => openComposer('pacing', 'Pacing · Total', 'praise')}
          className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left"
        >
          <div className="mb-0.5 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
            TOTAL
          </div>
          <div className="font-mono text-[22px] font-bold text-(--cd-text)">
            {pacing.total}
          </div>
          <div className="mt-0.5 text-[11px] text-(--cd-text-faint)">
            {pacing.totalNote}
          </div>
        </button>

        <div className="flex flex-col gap-1.5 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5">
          <div className="text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
            COMPANY TARGET RANGES
          </div>
          {pacing.companyRows.map((row) => (
            <button
              key={row.company}
              type="button"
              onClick={() =>
                openComposer(
                  'pacing',
                  `Pacing · ${row.company} target`,
                  'critique',
                )
              }
              className="flex items-center gap-2.5 rounded-md px-1 py-1 text-left hover:bg-black/10"
            >
              <CompanyIcon company={row.company} />
              <span className="w-[70px] shrink-0 text-[12.5px] font-semibold text-(--cd-text)">
                {row.company}
              </span>
              <span className="w-[84px] shrink-0 font-mono text-xs text-(--cd-text-muted)">
                {row.range}
              </span>
              <VerdictPill verdict={VERDICT_KEY[row.verdict]} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2.5">
        {pacing.topicAvg && (
          <AvgTile
            tile={pacing.topicAvg}
            onClick={() =>
              openComposer('pacing', 'Pacing · Topic avg', 'praise')
            }
          />
        )}
        {pacing.subtopicAvg && (
          <AvgTile
            tile={pacing.subtopicAvg}
            onClick={() =>
              openComposer('pacing', 'Pacing · Subtopic avg', 'praise')
            }
          />
        )}
      </div>
    </SectionCard>
  )
}

/**
 * Coding-only Pacing card (LG-632): the real total and nothing else. Takes
 * the stored duration string rather than a `PacingData` so a benchmark, avg
 * or note can never leak back in through the prop. The section-level
 * composer targets `Pacing · Total` as a critique — with benchmarks gone,
 * the total is the only thing in the section a comment can be about, and a
 * coach reaching for the header action on a bare duration is flagging it,
 * not praising it (the tile click keeps the base praise default).
 */
export function CodingPacingSection(props: {
  total: string
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { total, comments = [], anchorRef } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'pacing')

  return (
    <SectionCard
      title="Pacing"
      commentCount={sectionComments.length}
      onAddComment={() => openComposer('pacing', 'Pacing · Total', 'critique')}
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {/* The 2-col grid survives the benchmark cut: the TOTAL tile keeps the
          width it would have beside the company box rather than stretching a
          single number across the card. */}
      <div className="grid grid-cols-[1fr_1.6fr] gap-2.5">
        <button
          type="button"
          onClick={() => openComposer('pacing', 'Pacing · Total', 'praise')}
          className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left"
        >
          <div className="mb-0.5 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
            TOTAL
          </div>
          <div className="font-mono text-[26px] font-bold text-(--cd-text)">
            {total}
          </div>
        </button>
      </div>
    </SectionCard>
  )
}

function AvgTile(props: {
  tile: PacingAvgTileData
  onClick: () => void
}): ReactNode {
  const { tile, onClick } = props
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
    >
      <div className="mb-0.5 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
        {tile.label}
      </div>
      <div className="text-sm font-semibold text-(--cd-text)">
        {tile.value}{' '}
        <span
          className="text-[11.5px] font-semibold"
          style={{ color: tile.over ? 'var(--cd-amber)' : 'var(--cd-green)' }}
        >
          {tile.delta}
        </span>
      </div>
    </button>
  )
}
